import React from 'react';
import clsx from 'clsx';
import { NavLink, NavLinkProps } from 'react-router-dom';
import { Link, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme: Theme) => ({
  link: {
    display: 'inline-flex',
    transition: theme.transitions.custom.fast
  },
  none: {
    color: 'inherit'
  },
  primary: {
    color: theme.palette.primary.contrastText,
    '&:hover': {
      color: theme.palette.primary.light,
      '& *[fill="red"]': {
        fill: theme.palette.primary.light
      }
    }
  },
  secondary: {
    color: theme.palette.secondary.contrastText,
    '&:hover': {
      color: theme.palette.secondary.light,
      '& *[fill="red"]': {
        fill: theme.palette.secondary.light
      }
    }
  },
  underline: {
    color: theme.palette.primary.contrastText,
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  body1: {
    ...theme.typography.body1
  },
  body1Bold: {
    ...theme.typography.body1Bold
  },
  body2: {
    ...theme.typography.body2
  },
  subtitle1: {
    ...theme.typography.subtitle1
  }
}));

const CustomRouterLink = React.forwardRef<
  never,
  Omit<NavLinkProps, 'component'>
>((props, ref) => <NavLink innerRef={ref} {...props} />);
CustomRouterLink.displayName = 'CustomRouterLink';

interface CustomLinkProps {
  className?: string;
  variant: 'none' | 'primary' | 'secondary' | 'underline';
  id: string;
  to: string;
  typography?: 'body1' | 'body1Bold' | 'body2' | 'subtitle1';
  onClick?: (event: React.MouseEvent) => void;
  external?: boolean;
  target?: string;
  children: React.ReactNode;
}

const CustomLink: React.FC<CustomLinkProps> = ({
  className,
  variant,
  id,
  to,
  typography = 'body1',
  onClick,
  external,
  target,
  children
}) => {
  const classes = useStyles();

  return (
    <Link
      component={external ? 'a' : CustomRouterLink}
      className={clsx(
        classes.link,
        classes[variant],
        classes[typography],
        className
      )}
      id={id}
      onClick={onClick}
      underline="none"
      target={target}
      {...(external ? { href: to } : { to })}
    >
      {children}
    </Link>
  );
};
CustomLink.displayName = 'CustomLink';

export default CustomLink;
